/**
 * Bar tabs 1–4 (SPEC §6.4). When the transport is stopped a tab selects the
 * bar shown in the grid (`select` mutation + "selected bar N"). While playing
 * the tab of the sounding bar pulses and the selection follows playback so
 * the grid (and `get_session.human.selected.bar`) show the bar being heard.
 */
import type { Store } from '../state/store-api';
import type { Controller, Engine } from '../app/controller';
import { BARS, STEPS_PER_BAR } from '../types';
import { h, injectStyle, setFlag } from './dom';

/** Dependencies for `mountBarTabs()`; the controller (optional) clears the pulse the moment the transport stops. */
export interface BarTabsDeps {
  store: Store;
  engine: Pick<Engine, 'isPlaying' | 'onStep'>;
  controller?: Pick<Controller, 'onTransportChange'>;
}

/** Mounted bar tabs: root element, playhead hook, dispose. */
export interface BarTabsHandle {
  el: HTMLElement;
  /** Mark the bar containing absolute `step` as playing (no-op when the engine is stopped). */
  setStep(step: number): void;
  dispose(): void;
}

const CSS = `
.bm-bars{display:flex;gap:.35rem;align-items:center;min-width:0}
.bm-bars__label{font-size:.7rem;letter-spacing:.06em;text-transform:uppercase;color:var(--fg-muted);margin-right:.25rem}
.bm-bar{flex:1 1 0;min-width:0;min-height:2rem;border:1px solid var(--border);border-radius:var(--radius-s);background:var(--surface-1);
  color:var(--fg-muted);font-variant-numeric:tabular-nums;font-weight:600;cursor:pointer;position:relative}
.bm-bar:hover{background:var(--surface-2);color:var(--fg)}
.bm-bar[aria-selected="true"]{background:var(--surface-3);color:var(--fg);border-color:var(--border-strong)}
.bm-bar[data-playing="true"]{border-color:var(--accent);color:var(--fg);animation:bm-pulse 1s var(--ease) infinite}
.bm-bar[data-playing="true"]::after{content:"";position:absolute;left:.35rem;top:50%;transform:translateY(-50%);width:.4rem;height:.4rem;border-radius:50%;background:var(--accent)}
`;

/** Mount the bar tabs into `container`. */
export function mountBarTabs(container: HTMLElement, deps: BarTabsDeps): BarTabsHandle {
  injectStyle('bm-bars-css', CSS);
  const { store, engine } = deps;
  const tabs: HTMLButtonElement[] = [];
  const el = h('div', { class: 'bm-bars', role: 'tablist', 'aria-label': 'Bars' }, h('span', { class: 'bm-bars__label', 'aria-hidden': 'true' }, 'Bar'));

  for (let bar = 0; bar < BARS; bar++) {
    const tab = h(
      'button',
      { type: 'button', class: 'bm-bar', role: 'tab', dataset: { bar: String(bar) }, 'aria-selected': 'false', 'aria-label': `Bar ${bar + 1}`, 'aria-keyshortcuts': String(bar + 1) },
      String(bar + 1),
    ) as HTMLButtonElement;
    tab.addEventListener('click', () => {
      if (store.get().ui.selectedBar === bar) return;
      store.apply({ type: 'select', bar }, { by: 'human', label: `selected bar ${bar + 1}` });
      store.logHuman({ kind: 'select', detail: `selected bar ${bar + 1}` });
    });
    tabs.push(tab);
    el.append(tab);
  }

  const renderSelected = () => {
    const sel = store.get().ui.selectedBar;
    tabs.forEach((t, i) => {
      t.setAttribute('aria-selected', String(i === sel));
      t.tabIndex = i === sel ? 0 : -1;
    });
  };
  renderSelected();
  const unsub = store.subscribe(renderSelected);

  let playingBar = -1;
  const setStep = (step: number) => {
    const bar = engine.isPlaying() ? Math.floor(((step % (BARS * STEPS_PER_BAR)) + BARS * STEPS_PER_BAR) % (BARS * STEPS_PER_BAR) / STEPS_PER_BAR) : -1;
    if (bar === playingBar) return;
    playingBar = bar;
    tabs.forEach((t, i) => setFlag(t, 'data-playing', i === bar, 'true'));
    if (bar >= 0 && store.get().ui.selectedBar !== bar) store.apply({ type: 'select', bar }, { by: 'human', label: 'follow playback' });
  };
  const offStep = engine.onStep(setStep);
  // No step arrives after a stop, so the pulse would otherwise stay on the last bar.
  const offTransport = deps.controller?.onTransportChange?.((playing) => {
    if (!playing) setStep(0);
  });

  // Roving focus with arrow keys inside the tablist.
  el.addEventListener('keydown', (e) => {
    const idx = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (idx < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = tabs[(idx + (e.key === 'ArrowRight' ? 1 : -1) + BARS) % BARS]!;
      next.focus();
      next.click();
    }
  });

  container.append(el);
  return {
    el,
    setStep,
    dispose() {
      unsub();
      offStep();
      offTransport?.();
      el.remove();
    },
  };
}
